import React, { FC } from "react";
import { Icon } from "Components/UI";
import { ITopicDnDResponse } from "Data/interfaces/Activities/IDragAndDropDataFormat";
import { dateToLocaleString, isExpiredDate } from "Utils/DateUtils";
import { handlePriority, handleVisibility } from "Utils/ActivitiesUtils";
import HighlighterWords from "Components/UI/HighlightWords";
import { useSelector } from "react-redux";
import { getTheme } from "Store/MultiDomain/MultiDomain.selector";
import AvatarsItems from "Pages/Activities/components/AvatarsItems";
import { getDisciplineDisplayIcon, getTypeIcon } from "Utils/ObraUtils";
import styles from "./ActivityCard.module.scss";

interface IActivityCard {
  activity: ITopicDnDResponse;
  searchTerm: string;
  onClick: () => void;
}

const ActivityCard: FC<IActivityCard> = ({ activity, searchTerm, onClick }) => {
  const theme = useSelector(getTheme);

  const hasComments = activity.CommentCount > 0;
  const commentsLength = activity.CommentCount > 99 ? '99+' : activity.CommentCount;
  const hasAttachments = activity.Attachments && activity.Attachments.length > 0;

  return (
    <div className={styles.card} onClick={onClick}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <span className={styles.identifier}>#{activity.Identifier}</span>
          <Icon
            icon={getTypeIcon(activity.Type?.Name || '')}
            customSize={12}
            customColor={'#BEC2C6'}
          />
          <span className={styles.type}>{activity.Type?.Name}</span>
        </div>
        <div className={styles.headerRight}>
          {hasComments && (
            <span className={styles.comments}>
              <Icon icon="doubleChat" customSize={14} />
              <span>{commentsLength}</span>
            </span>
          )}
          {hasAttachments && (
            <span className={styles.attachments}>
              <Icon icon="paperclip" customSize={14} />
              <span>{activity.Attachments.length}</span>
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <HighlighterWords
        text={activity.Title}
        wordSearched={searchTerm}
        maxLength={80}
        className={styles.title}
      />

      {/* Assigned Users */}
      {activity.TopicAssignedUsers && activity.TopicAssignedUsers.length > 0 && (
        <div className={styles.avatars}>
          <AvatarsItems
            users={activity.TopicAssignedUsers}
            showDetail={false}
          />
        </div>
      )}

      {/* Disciplines */}
      {activity.ConstructionSiteDisciplines && activity.ConstructionSiteDisciplines.length > 0 && (
        <div className={styles.disciplines}>
          {activity.ConstructionSiteDisciplines.slice(0, 2).map((discipline, index) => (
            <div key={`${discipline.ConstructionSiteDisciplinesId}-${index}`} className={styles.discipline}>
              <Icon
                icon={getDisciplineDisplayIcon(discipline)}
                customSize={12}
              />
              <span>{discipline?.CustomName || discipline?.Discipline?.Name}</span>
            </div>
          ))}
          {activity.ConstructionSiteDisciplines.length > 2 && (
            <span className={styles.moreCount}>+{activity.ConstructionSiteDisciplines.length - 2}</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        {/* Due Date */}
        {activity.DueDate && (
          <div className={styles.dueDate}>
            <Icon
              icon="calendar"
              customSize={14}
              customColor={isExpiredDate(activity.DueDate) ? '#E2574C' : '#BEC2C6'}
            />
            <span style={{ color: isExpiredDate(activity.DueDate) ? '#E2574C' : '#6B6B6B' }}>
              {dateToLocaleString(activity.DueDate)}
            </span>
          </div>
        )}

        {/* Tags */}
        <div className={styles.tags}>
          {/* Visibility */}
          {(activity.Visibility || activity.Visibility === 0) && (
            <div className={styles.tag}>
              <Icon
                icon={handleVisibility(activity.Visibility, theme).icon}
                customSize={handleVisibility(activity.Visibility, theme).size}
                customColor={handleVisibility(activity.Visibility, theme).color}
              />
              <span>{handleVisibility(activity.Visibility, theme).label}</span>
            </div>
          )}

          {/* Priority */}
          {activity.Priority && (
            <div className={styles.tag} style={{ color: handlePriority(activity.Priority, theme).color }}>
              <Icon
                icon={handlePriority(activity.Priority, theme).icon}
                customSize={handlePriority(activity.Priority, theme).size}
                customColor={handlePriority(activity.Priority, theme).color}
              />
              <span>{handlePriority(activity.Priority, theme).label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Indicator */}
      {activity.Status && (
        <div
          className={styles.statusBar}
          style={{ backgroundColor: activity.Status.Color }}
        />
      )}
    </div>
  );
};

export default ActivityCard;
